import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Input, Select, SelectItem, Spinner } from '@nextui-org/react';
import { IconArrowLeft, IconEye, IconEyeOff } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { IconImageEdit } from '../../assets/icons';
import { Constants, classNames, formatAmount, fromPhotos, getOnlyNumbers } from '../../utils';
import { AuthService } from '../../services';
import { usePayment } from '../../hooks/usePayment';
import CountryFlag from '../../components/country-flag';
import Render from '../../components/render';
import AcquirePlan from '../payment/acquire-plan';

const STEP = {
    DATA: 1,
    PAYMENT: 2,
};

const RegisterSchool = () => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const toggleVisibility = () => setIsVisible(!isVisible);
    const { countries, documentTypes, form, initiatePayment, isLoading, isValidForm, onChange, onChangeCountry, onChangeState, onSuccess, onUpload, phoneCodes, plans, setShowPaymentModal, setStep, showPaymentModal, states, step, submit, townships, user } = useForm();
    const _isValidForm = isValidForm(false);

    const onChangePlan = (plan) => {
        onChange(plan.id, 'plan_id');
        onChange(plan.price, 'amount');
    };

    const request = useMemo(() => ({
        amount: form.amount,
        plan_id: form.plan_id,
        plan: plans.find(x => x.id === form.plan_id),
    }), [form, plans]);

    return (
        <div>
            <Render condition={showPaymentModal}>
                <AcquirePlan
                    user={user}
                    plan={request.plan}
                    initPayment={initiatePayment}
                    onSuccess={onSuccess}
                    onClose={() => setShowPaymentModal(false)}
                />
            </Render>

            {isLoading && (
                <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
                    <Spinner />
                </div>
            )}

            <div className="px-4 md:px-0 flex justify-center">
                <div
                    className="absolute top-4 left-4 cursor-pointer"
                    onClick={() => step === STEP.PAYMENT ? setStep(STEP.DATA) : navigate('/register')}
                >
                    <IconArrowLeft />
                </div>

                <div className="w-[420px] py-4 px-0">
                    <p className="text-center text-lg font-bold text-secondary mb-8">Registro de colegio</p>
                    <div className="flex flex-row items-start gap-4">
                        <div className="flex-1 text-center">
                            <div className="h-[6px] rounded bg-secondary" />
                            <span className="text-sm font-semibold text-gray">Datos</span>
                        </div>
                        <div className="flex-1 text-center">
                            <div className={classNames('h-[6px] rounded', step === STEP.PAYMENT ? 'bg-secondary' : 'bg-gray')} />
                            <span className="text-sm font-semibold text-gray">Plan</span>
                        </div>
                    </div>

                    {step === STEP.DATA && (
                        <>
                            <label htmlFor="photo" className="cursor-pointer text-center border-2 border-gray w-24 h-24 rounded-lg flex justify-center items-center self-center my-4 mx-auto overflow-hidden">
                                {!!form.photoUrl ? (
                                    <img className="w-full h-full object-cover" src={form.photoUrl} alt="Foto del colegio" />
                                ) : (
                                    <img className="w-16 h-16 object-contain opacity-50" src={IconImageEdit} alt="Foto del colegio" />
                                )}
                                <input id="photo" name="photo" type="file" accept="image/*" hidden onChange={onUpload} />
                            </label>

                            <div className="flex flex-col gap-4">
                                <div className='flex flex-col md:flex-row gap-4 [&>div]:flex-1'>
                                    <Input
                                        label="Nombre"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        value={form.name}
                                        onValueChange={v => onChange(v, 'name')}
                                    />

                                    <Input
                                        label="RIF/NIT"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        // startContent={(
                                        //     <Select
                                        //         aria-label="RIF/NIT"
                                        //         variant="flat"
                                        //         classNames={{ trigger: 'border-0 max-w-24' }}
                                        //         disallowEmptySelection={true}
                                        //         selectedKeys={form.document_type_id ? [form.document_type_id] : ''}
                                        //         onSelectionChange={v => onChange(v.currentKey, 'document_type_id')}
                                        //     >
                                        //         {documentTypes.map((item) => (
                                        //             <SelectItem key={item.value} value={item.value}>
                                        //                 {item.label}
                                        //             </SelectItem>
                                        //         ))}
                                        //     </Select>
                                        // )}
                                        variant="bordered"
                                        radius="sm"
                                        classNames={{ innerWrapper: 'overflow-hidden', inputWrapper: 'pl-0' }}
                                        value={form.document}
                                        onValueChange={v => onChange(v, 'document')}
                                    />
                                </div>

                                <div className='flex gap-4'>
                                    <Input
                                        label="Director(a)"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        value={form.director}
                                        onValueChange={v => onChange(v, 'director')}
                                    />
                                    <Input
                                        label="DNI/C.I"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        startContent={(
                                            <Select
                                                aria-label="Cédula del director"
                                                variant="flat"
                                                disabled={true}
                                                classNames={{ trigger: 'border-0 max-w-24' }}
                                                disallowEmptySelection={true}
                                                selectedKeys={form.document_type_director ? [form.document_type_director] : ''}
                                                onSelectionChange={v => onChange(v.currentKey, 'document_type_director')}
                                            >
                                                {documentTypes.map((item) => (
                                                    <SelectItem key={item.value} value={item.value}>
                                                        {item.label}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        )}
                                        variant="bordered"
                                        radius="sm"
                                        classNames={{ innerWrapper: 'overflow-hidden', inputWrapper: 'pl-0' }}
                                        value={form.document_director}
                                        onValueChange={v => onChange(v, 'document_director')}
                                    />
                                </div>

                                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                                    <Input
                                        label="Teléfono"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        startContent={(
                                            <Select
                                                variant="flat"
                                                classNames={{ trigger: 'border-0 max-w-32 pl-1 pr-0' }}
                                                disallowEmptySelection={true}
                                                selectedKeys={(!!form.phonecode_id && !!phoneCodes.length) ? [form.phonecode_id] : []}
                                                onSelectionChange={v => onChange(v.currentKey, 'phonecode_id')}
                                                startContent={<CountryFlag phoneCodeId={form.phonecode_id} phoneCodes={phoneCodes} />}
                                            >
                                                {phoneCodes.map((item) => (
                                                    <SelectItem
                                                        key={item.value}
                                                        value={item.value}
                                                        startContent={<CountryFlag src={item.url} />}
                                                        classNames={{ title: 'min-w-48' }}
                                                    >
                                                        {item.label}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        )}
                                        variant="bordered"
                                        radius="sm"
                                        classNames={{ innerWrapper: 'overflow-hidden', inputWrapper: 'pl-0' }}
                                        value={form.phone}
                                        onValueChange={v => onChange(getOnlyNumbers(v), 'phone')}
                                    />
                                    <Input
                                        label="Correo"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        value={form.email}
                                        onValueChange={v => onChange(v, 'email')}
                                    />
                                </div>
                                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                                    <Select
                                        label="País"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        classNames={{ label: 'font-semibold' }}
                                        disallowEmptySelection={true}
                                        selectedKeys={!!form.country_id ? [form.country_id] : []}
                                        onSelectionChange={onChangeCountry}
                                    >
                                        {countries.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                    <Select
                                        label={!form.country_id ? 'Estado/Departamento' : form.country_id == Constants.COUNTRIES.VEN ? 'Estado' : 'Departamento'}
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        classNames={{ label: 'font-semibold' }}
                                        disallowEmptySelection={true}
                                        selectedKeys={!!form.state_id ? [form.state_id] : []}
                                        onSelectionChange={onChangeState}
                                        isDisabled={!form.country_id}
                                    >
                                        {states.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                </div>
                                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                                    <Select
                                        label="Municipio"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        classNames={{ label: 'font-semibold' }}
                                        disallowEmptySelection={true}
                                        selectedKeys={!!form.municipality_id ? [form.municipality_id] : []}
                                        onSelectionChange={v => onChange(v.currentKey, 'municipality_id')}
                                        isDisabled={!form.state_id}
                                    >
                                        {townships.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </Select>
                                    <div className="hidden md:flex" />
                                </div>

                                <Input
                                    label="Código DEA"
                                    labelPlacement="outside"
                                    placeholder=" "
                                    radius="sm"
                                    variant="bordered"
                                    value={form.dea_code}
                                    onValueChange={v => onChange(v, 'dea_code')}
                                />

                                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                                    <Input
                                        label="Coordinador(a) de CE"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        value={form.coordinator}
                                        onValueChange={v => onChange(v, 'coordinator')}
                                    />

                                    <Input
                                        label="DNI/C.I"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        startContent={(
                                            <Select
                                                aria-label="Cédula del Coordinador"
                                                variant="flat"
                                                disabled={true}
                                                classNames={{ trigger: 'border-0 max-w-24' }}
                                                disallowEmptySelection={true}
                                                selectedKeys={form.document_type_coordinator ? [form.document_type_coordinator] : ''}
                                                onSelectionChange={v => onChange(v.currentKey, 'document_type_coordinator')}
                                            >
                                                {documentTypes.map((item) => (
                                                    <SelectItem key={item.value} value={item.value}>
                                                        {item.label}
                                                    </SelectItem>
                                                ))}
                                            </Select>
                                        )}
                                        variant="bordered"
                                        radius="sm"
                                        classNames={{ innerWrapper: 'overflow-hidden', inputWrapper: 'pl-0' }}
                                        value={form.document_coordinator}
                                        onValueChange={v => onChange(v, 'document_coordinator')}
                                    />
                                </div>

                                <Input
                                    label="Dirección"
                                    labelPlacement="outside"
                                    placeholder=" "
                                    variant="bordered"
                                    radius="sm"
                                    value={form.address}
                                    onValueChange={v => onChange(v, 'address')}
                                />
                                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                                    <Input
                                        label="Zona educativa"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        value={form.educational_center}
                                        onValueChange={v => onChange(v, 'educational_center')}
                                    />
                                    <div className="hidden md:flex" />
                                </div>
                                <div className="flex flex-col md:flex-row gap-4 [&>div]:flex-1">
                                    <Input
                                        label="Contraseña"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        value={form.password}
                                        onValueChange={v => onChange(v, 'password')}
                                        type={isVisible ? "text" : "password"}
                                        endContent={
                                            <button className="focus:outline-none" type="button" onClick={toggleVisibility}>
                                                {isVisible ? (
                                                    <IconEyeOff className="w-6 h-6" />
                                                ) : (
                                                    <IconEye className="w-6 h-6" />
                                                )}
                                            </button>
                                        }
                                    />
                                    <Input
                                        label="Repetir contraseña"
                                        labelPlacement="outside"
                                        placeholder=" "
                                        variant="bordered"
                                        radius="sm"
                                        value={form.password_confirmation}
                                        onValueChange={v => onChange(v, 'password_confirmation')}
                                        type={isVisible ? "text" : "password"}
                                        endContent={
                                            <button className="focus:outline-none" type="button" onClick={toggleVisibility}>
                                                {isVisible ? (
                                                    <IconEyeOff className="w-6 h-6" />
                                                ) : (
                                                    <IconEye className="w-6 h-6" />
                                                )}
                                            </button>
                                        }
                                    />
                                </div>
                            </div>

                            <div className="flex justify-center">
                                <Button
                                    type="button"
                                    className="w-1/2 bg-green text-white text-md my-12"
                                    onPress={() => _isValidForm ? setStep(STEP.PAYMENT) : isValidForm()}
                                >
                                    Siguiente
                                </Button>
                            </div>
                        </>
                    )}

                    {step === STEP.PAYMENT && (
                        <>
                            <main className="flex my-12 justify-center">
                                {plans.map(plan => {
                                    const isFree = plan.price === 0;
                                    const description = String(plan?.description ?? '')
                                        .replace(':date', moment().add(1, 'week').format('DD/MM/YYYY'))
                                        .replace(':price', '$' + formatAmount(plan?.price));

                                    return (
                                        <div
                                            className={classNames(
                                                'rounded-xl p-2 cursor-pointer shadow-md',
                                                form.plan_id === plan.id ? 'bg-[#C6FFDE]' : 'bg-white'
                                            )}
                                            onClick={() => onChangePlan(plan)}
                                            key={plan.id}
                                        >
                                            <div className="flex justify-start items-start gap-4">
                                                <div className="bg-[#3E82C3] p-2 rounded-lg">
                                                    <img className="w-7 h-7 object-contain" src={fromPhotos(plan?.icon)} alt="Plan" />
                                                </div>

                                                <div>
                                                    <p className="font-bold text-sm leading-4 mb-0">{plan?.name}</p>
                                                    <p className="font-bold text-xl text-secondary mb-0">
                                                        { isFree ? '' : formatAmount(plan?.price, '$') }
                                                    </p>
                                                </div>
                                            </div>

                                            <p className="mt-4 font-medium text-xs text-black whitespace-pre-line">
                                                {description}
                                            </p>
                                        </div>
                                    )
                                })}
                            </main>

                            <div className="flex flex-col items-center">
                                <Button
                                    type="button"
                                    className="w-1/2 bg-green text-white text-md"
                                    onPress={submit}
                                    isDisabled={isLoading || !_isValidForm}
                                >
                                    Registrarme
                                </Button>

                                <Button
                                    type="button"
                                    variant="flat"
                                    className="w-1/2 text-md mt-4"
                                    onPress={() => setStep(STEP.DATA)}
                                    isDisabled={isLoading}
                                >
                                    Atrás
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ---- Nuevo useForm ----

const useForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [step, setStep] = useState(STEP.DATA);
    const [form, setForm] = useState({
        level_id: Constants.USER.LEVELS.SCHOOL,
        photo: {},
        photoUrl: '',
        name: '',
        director: '',
        document_director: '',
        phonecode_id: '',
        phone: '',
        email: '',
        country_id: '',
        document_type_id: '',
        document_type_coordinator: '',
        document_type_director: '',
        document: '',
        state_id: '',
        municipality_id: '',
        address: '',
        educational_center: '',
        password: '',
        password_confirmation: '',
        dea_code: '',
        coordinator: '',
        document_coordinator: '',
        plan_id: undefined,
        amount: 0,
        hasFile: true,
    });
    const [user, setUser] = useState();
    const [phoneCodes, setPhoneCodes] = useState([]);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [townships, setTownships] = useState([]);
    const [plans, setPlans] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const { initiatePayment } = usePayment({
        onCompletePayment: (data) => {
            if (!data.success) return deleteUser(data.paymentData.user_id);
            onSuccess(data.paymentData);
            setTimeout(() => window.location.reload(), 1000);
        }
    })

    const onUpload = (e) => {
        setForm(s => ({
            ...s,
            photo: e.target.files[0],
            photoUrl: URL.createObjectURL(e.target.files[0]),
        }));
    }

    const onChange = (value, target) => {
        console.log('value: ', value)
        console.log('target: ', target)
        setForm(s => ({ ...s, [target]: value }));
    }

    // NUEVO: Cambiar país
    const onChangeCountry = async (selected) => {
        const country_id = selected.currentKey;
        setForm(s => ({
            ...s,
            country_id,
            state_id: '',
            municipality_id: ''
        }));
        setStates([]); setTownships([]);
        if (country_id) {
            await getStatesByCountry(country_id);
        }
    };

    // NUEVO: Cambiar estado
    const onChangeState = async (selected) => {
        const state_id = selected.currentKey;
        setForm(s => ({
            ...s,
            state_id,
            municipality_id: ''
        }));
        setTownships([]);
        if (state_id) {
            await getMunicipalitiesByState(state_id);
        }
    };

    // Validación igual que antes...
    const isValidForm = (showError = true) => {
        const onError = (msg) => {
            if (showError) toast.error(msg);
            return false;
        }
        // ... mismo código de validación ...
        if (step === STEP.DATA) {
            if (!form.name)
                return onError('Debe indicar el nombre del colegio');

            if (!form.director)
                return onError('Debe indicar el nombre del director');

            if (!form.phonecode_id)
                return onError('Debe indicar el código de área');

            if (!form.document_director)
                return onError('Debe indicar la cédula del director');

            if (!form.document)
                return onError('Debe indicar el rif');

            if (!form.phone)
                return onError('Debe indicar el número telefónico');

            if (!form.email)
                return onError('Debe indicar el correo');

            if (!form.email.match(/^[A-Za-z0-9._%+-]+@[a-zA-Z_]+?\.[a-zA-Z]{2,}$/))
                return onError('El correo debe tener un formato válido');

            if (!form.country_id)
                return onError('Debe indicar el país');

            if (!form.state_id)
                return onError('Debe indicar el estado');

            if (!form.municipality_id)
                return onError('Debe indicar el municipio');

            if (!form.address)
                return onError('Debe indicar la dirección');

            if (!form.educational_center)
                return onError('Debe indicar la zona educativa');

            if (!form.password)
                return onError('Debe indicar la contraseña');

            if (form.password !== form.password_confirmation)
                return onError('Las contraseñas no coinciden');
        } else {
            if (!form.plan_id)
                return onError('Debe seleccionar un plan');
        }
        return true;
    }

    const deleteUser = async (userId) => {
        try {
            await AuthService.destroyUser(userId);
        } catch (error) {
            toast.error(String(error) || 'Ocurrió un error inesperado');
        }
    }

    const submit = async () => {
        if (!isValidForm()) return;
        setIsLoading(true);

        try {
            if (!user) {
                const res = await AuthService.register(form);
                // Se almacena el usuario registrado por si el mismo sale del modal del pago (no vuelve a registrarse)
                setUser(res.user);
                onSuccess(res);
            }
            // setShowPaymentModal(true);

        } catch (error) {
            toast.error(error || 'Ocurrió un error inesperado');
        }
        setIsLoading(false);
    }

    const onSuccess = (res) => {
        if (res?.user) {
            dispatch({ type: 'SET_USER', payload: res?.user });
        } else {
            // La respuesta fue exitosa pero no devolvió el usuario
            toast.error('Ocurrió un error inesperado. Por favor, intente iniciar sesión');
        }
        setTimeout(() => {
            navigate('/');
            window.location.reload();
        }, 100);
    }

    // Llamada original solo para países, phonecodes, plans y doc types.
    const getSelects = async () => {
        const toSelect = (items) => items.map(x => ({ ...x, value: String(x.id), label: x.name }));

        try {
            const selects = await AuthService.getSelects();
            setPhoneCodes(toSelect(selects.phonecodes));
            setCountries(toSelect(selects.countries));
            setPlans(selects.plans.filter(x => x.for === Constants.PLAN_FOR.SCHOOL && x.price === 0));
            setDocumentTypes(toSelect(selects.document_types));
        } catch (error) {
            toast.error(String(error) || 'Ocurrió un error inesperado');
            return false;
        }
    }

    // NUEVO: Obtener estados por país
    const getStatesByCountry = async (country_id) => {
        console.log('country_id: ', country_id)
        setIsLoading(true);
        try {
            const { states } = await AuthService.getStates({ country_id });
            setStates(states.map(x => ({ ...x, value: String(x.id), label: x.name })));
        } catch (error) {
            toast.error('Error al obtener los estados');
        }
        setIsLoading(false);
    }

    // NUEVO: Obtener municipios por estado
    const getMunicipalitiesByState = async (state_id) => {
        setIsLoading(true);
        try {
            const { municipalities } = await AuthService.getMunicipalities({ state_id });
            setTownships(municipalities.map(x => ({ ...x, value: String(x.id), label: x.name })));
        } catch (error) {
            toast.error('Error al obtener los municipios');
        }
        setIsLoading(false);
    }

    useEffect(() => {
        getSelects();
    }, []);

    return {
        countries,
        documentTypes,
        form,
        initiatePayment,
        isLoading,
        isValidForm,
        onChange,
        onChangeCountry,
        onChangeState,
        onSuccess,
        onUpload,
        phoneCodes,
        plans,
        setShowPaymentModal,
        setStep,
        showPaymentModal,
        states,
        step,
        submit,
        townships,
        user,
    };
};

export default RegisterSchool;